/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ContractDatepickerHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDatepickerHeaderItem';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import ContractInputHeaderItem from '@/components/common/ContractInputHeaderItem/ContractInputHeaderItem';
import PageTitle from '@/components/common/PageTitle/PageTitle';
import SectionCard from '@/components/common/SectionCard/SectionCard';
import { GetCurrencyTypes } from '@/graphql/contracts/_generated_/getCurrencyTypes.query';
import { SearchCustomers } from '@/graphql/contracts/_generated_/searchCustomers.query';
import { getEnabledCurrencies } from '@/utils/data-helpers/currecies';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { contractsBCPath } from '@/utils/path.utlis';
import useCreateContract from './hooks/useCreateContract';
import useSearchCustomers from './hooks/useSearchCustomers';
import { contractHeadersIntialValues, contractHeadersValidationSchema } from './validation/contractHeaders.validation';
import { GetContractOrigins } from '@/graphql/contracts/_generated_/getContractOrigins.query';
import { getContractOrigins } from '@/utils/data-helpers/contractOrigins';
import { ContractOriginEnum } from '@/graphql/contracts/_generated_/contracts';
import useGetClientDatabases
  from '../ContractDetails/RightPanel/Billing/EditContractHeader/hooks/useGetClientDatatbases';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { NumberUtils } from '@/utils/numbers.utils';

export type Customer = NonNullable<SearchCustomers['searchContractsAndCustomers']>['customers'][number];

export default function AddContractContainer() {
  const { createContract, createdContract, isLoadingCreateContract } = useCreateContract();
  const { isSearchLoading, searchCustomers, searchCustomersData } = useSearchCustomers();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const allowedRole = (
    userRole == UserRole.FULL_ACCESS ||
    userRole == UserRole.ADMIN ||
    userRole == UserRole.USER
  );

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  const { databaseList } = useGetClientDatabases();
  const routerHistory = useHistory();

  if (createdContract) {
    setTimeout(() => {
      routerHistory.push(getParameterizedLink(`/${contractsBCPath}/${createdContract.contractID}/add-contract-line/`));
    });
  }

  return (
    <Formik
      initialValues={contractHeadersIntialValues}
      validationSchema={contractHeadersValidationSchema}
      validateOnMount={true}
      onSubmit={(values) => {
        if (!isLoadingCreateContract) {
          createContract({
            contractNumber: values.contractNumber,
            contractStartDate: values.contractStartDate,
            customerID: values.customerID,
            term: NumberUtils.parseIntegerInput(values.term.toString()),
            renewableTerm: NumberUtils.parseIntegerInput(values.renewableTerm.toString()),
            currency: values.currency,
            origin: values.origin ?? ContractOriginEnum.Dimensions,
            shipToCustomerID: values.shipToCustomerId,
            invoiceToCustomerID: values.invoiceToCustomerId,
            clientDatabaseReference: values.clientDatabaseId,
          });
        }
      }}
    >
      {({ errors, setFieldValue, setFieldTouched, touched, values, isValid }) => (
        <Form>
          <div className="mb-20">
            <PageTitle title="Add Contract" />
          </div>

          <div className="mb-20">
            <SectionCard
              title={{ titleText: 'Contract Header' }}
              collapsable={{ buttonText: 'Contract Header' }}
              padding={true}
            >
              <div className="row">
                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-25">
                  <ContractDropdownHeaderItem<Customer>
                    required={true}
                    label="Contracted To"
                    id="customerId"
                    placeholder="John Doe Ltd."
                    getOptionLabel={(option) =>
                      `[${option.customerAccountNumber}] - ${option.customerName} ${option.postCode ? `, ${option.postCode}` : ''}`}
                    getOptionValue={(option) => option.customerID}
                    handleInputChange={(value) => searchCustomers(value)}
                    optionsData={searchCustomersData || []}
                    handleChange={(option) => {
                      setFieldTouched('customerID');
                      setFieldValue('customerID', option?.customerID ?? '');
                    }}
                    isLoading={isSearchLoading}
                    errorMessage={touched.customerID && errors.customerID ? errors.customerID : false}
                    isSearchable={true}
                  />
                </div>

                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-25">
                  <ContractInputHeaderItem
                    required={true}
                    id="contractNumber"
                    label="Contract Number"
                    placeholder="XYZ001"
                    onChange={(value: string) => {
                      setFieldTouched('contractNumber');
                      setFieldValue('contractNumber', value);
                    }}
                    value={values.contractNumber}
                    errorMessage={touched.contractNumber && errors.contractNumber ? errors.contractNumber : false}
                  />
                </div>

                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-20">
                  <ContractDatepickerHeaderItem
                    required={true}
                    id="contractStartDate"
                    label="Contract Start Date"
                    onChange={({ value }) => {
                      setFieldTouched('contractStartDate');
                      setFieldValue('contractStartDate', value);
                    }}
                    value={values.contractStartDate}
                    errorMessage={
                      touched.contractStartDate && errors.contractStartDate
                        ? errors.contractStartDate
                        : false
                    }
                  />
                </div>

                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-20">
                  <ContractInputHeaderItem
                    required={true}
                    type="number"
                    min={0}
                    step={1}
                    id="term"
                    label="Contract Term (Months)"
                    onChange={(value: string) => {
                      setFieldTouched('term');
                      setFieldValue('term', value);
                    }}
                    value={values.term}
                    errorMessage={touched.term && errors.term ? errors.term : false}
                  />
                </div>

                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-20">
                  <ContractInputHeaderItem
                    required={true}
                    type="number"
                    min={0}
                    step={1}
                    id="renewableTerm"
                    label="Renewable Term (Months)"
                    onChange={(value: string) => {
                      setFieldTouched('renewableTerm');
                      setFieldValue('renewableTerm', value);
                    }}
                    value={values.renewableTerm}
                    errorMessage={touched.renewableTerm && errors.renewableTerm ? errors.renewableTerm : false}
                  />
                </div>

                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <ContractDropdownHeaderItem<GetCurrencyTypes['currencyTypes'][number]>
                    label="Currency"
                    id="currency"
                    required={true}
                    getOptionLabel={(option) => option.formattedCurrency}
                    getOptionValue={(option) => option.numericCode}
                    handleInputChange={(value) => searchCustomers(value)}
                    optionsData={getEnabledCurrencies() || []}
                    handleChange={(option) => {
                      setFieldTouched('currency');
                      setFieldValue('currency', option?.currency ?? '');
                    }}
                    errorMessage={touched.currency && errors.currency ? errors.currency : false}
                  />
                </div>
                <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <ContractDropdownHeaderItem<GetContractOrigins['contractOrigins'][number]>
                    label="Origin"
                    id="origin"
                    required={true}
                    getOptionLabel={(option) => option.formattedKey}
                    getOptionValue={(option) => option.key}
                    optionsData={(getContractOrigins() || []).filter(
                      (o) => o.key !== ContractOriginEnum.Import && o.key !== ContractOriginEnum.AutoAttach
                    )}
                    handleChange={(option) => {
                      console.log(isValid, values.origin);
                      setFieldTouched('origin');
                      setFieldValue('origin', option?.key ?? ContractOriginEnum.Dimensions);
                    }}
                    errorMessage={touched.origin && errors.origin ? errors.origin : false}
                  />
                </div>
                {allowedRole && <>
                  <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-25">
                    <ContractDropdownHeaderItem<Customer>
                      required={false}
                      label="Ship To"
                      id="shipToCustomerId"
                      placeholder="John Doe Ltd."
                      isClearable={true}
                      getOptionLabel={(option) =>
                        `[${option.customerAccountNumber}] - ${option.customerName} ${option.postCode ? `, ${option.postCode}` : ''}`}
                      getOptionValue={(option) => option.customerID}
                      handleInputChange={(value) => searchCustomers(value)}
                      optionsData={searchCustomersData || []}
                      handleChange={(option) => {
                        setFieldTouched('shipToCustomerId');
                        setFieldValue('shipToCustomerId', option?.customerID);
                      }}
                      isLoading={isSearchLoading}
                      errorMessage={touched.customerID && errors.customerID ? errors.customerID : false}
                      isSearchable={true}
                    />
                  </div>
                  <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-25">
                    <ContractDropdownHeaderItem<Customer>
                      required={false}
                      label="Invoice To"
                      id="invoiceToCustomerId"
                      placeholder="John Doe Ltd."
                      isClearable={true}
                      getOptionLabel={(option) =>
                        `[${option.customerAccountNumber}] - ${option.customerName} ${option.postCode ? `, ${option.postCode}` : ''}`}
                      getOptionValue={(option) => option.customerID}
                      handleInputChange={(value) => searchCustomers(value)}
                      optionsData={searchCustomersData || []}
                      handleChange={(option) => {
                        setFieldTouched('invoiceToCustomerId');
                        setFieldValue('invoiceToCustomerId', option?.customerID);
                      }}
                      isLoading={isSearchLoading}
                      errorMessage={touched.customerID && errors.customerID ? errors.customerID : false}
                      isSearchable={true}
                    />
                  </div>
                  <div className="col-xxl-4 col-xl-6 col-lg-6 col-md-12 col-sm-12 mb-sm-25">
                    <ContractDropdownHeaderItem<ClientDatabaseType>
                      required={false}
                      label="Client Database"
                      id="clientDatabaseId"
                      placeholder="John Doe Ltd."
                      isClearable={true}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.iD}
                      handleInputChange={(value) => databaseList.find((x) => x?.name?.includes(value))}
                      optionsData={databaseList || []}
                      handleChange={(option) => {
                        setFieldTouched('clientDatabaseId');
                        setFieldValue('clientDatabaseId', option?.iD);
                      }}
                      isLoading={isSearchLoading}
                      errorMessage={touched.customerID && errors.customerID ? errors.customerID : false}
                      isSearchable={true}
                    />
                  </div>
                </>
                }
              </div>
            </SectionCard>
          </div>

          <div>
            <Button
              type="submit"
              buttonStyleType={ButtonStyleType.PRIMARY}
              disabled={!isValid}
            >
              Create Contract
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
